/**
 * CPV-01 S6 · the CPV screens' one door to the server (/bp/cpv/…).
 *
 * The client decides nothing: every act is one request, a signed act carries its fresh e-signature in the same body
 * (`esign: {username, password, comment}` — services/approval/guards.py), and a refusal is shown with the server's
 * own code and message (`refusalOf`). The person is always the session's; no body ever names one.
 */
import Axios from 'axios';

const BP = '/bp';
/** Q8p · the shared plan charts (sharedPlanCharts): reused for CHARTS_REUSE_MS, dropped on every write. */
const CHARTS_REUSE_MS = 60 * 1000;      // one minute: the time a person moves between a plan's chart screens
const chartsCache = new Map();          // `${plan}|${full}` → { at, promise }
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('Token') || ''}` } });
const unwrap = (r) => r.data?.data ?? r.data;

export const getJson = (path, params) => Axios.get(`${BP}${path}`, { params, ...auth() }).then(unwrap);
/** Every write may change what a chart shows (a lock, an exclusion, a sheet's approval, an import): the shared plan
 * charts are dropped — here and in the other CPV doors that write (cpvMasterApi). */
export const dropSharedCharts = () => chartsCache.clear();
/** The cache is tab-local, so another person's signature cannot reach it: coming back to the tab (focus, or the page
 * turning visible) drops it, and the next chart screen reads anew. */
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.addEventListener('focus', dropSharedCharts);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') dropSharedCharts(); });
}
export const postJson = (path, body) => { dropSharedCharts(); return Axios.post(`${BP}${path}`, body, auth()).then(unwrap); };
export const patchJson = (path, body) => { dropSharedCharts(); return Axios.patch(`${BP}${path}`, body, auth()).then(unwrap); };

/** The server's refusal: { code, message, status, extra } — never re-worded. */
export function refusalOf(err, fallback = 'The server refused this.') {
  const b = err?.response?.data || {};
  const { code, message, status: _s, detail, ...extra } = b;
  return {
    code: code || (err?.response ? `HTTP ${err.response.status}` : 'NO_ANSWER'),
    message: message || detail || fallback,
    status: err?.response?.status || null,
    extra,
  };
}

/** A fresh signature for one act, in the body shape the server reads. */
export const esignOf = ({ username, password, comment, reason }) => ({ username, password, comment: comment ?? reason ?? '' });

/**
 * Q8p · the plan charts are one server computation per answer (every parameter of the plan read and evaluated), so the
 * screens share them: a request already in flight for the same plan and `full` is joined, and an answer is reused while
 * it is younger than CHARTS_REUSE_MS (overview → parameter → back opens no new computation). `fresh` (the Refresh
 * button) always reads anew. A refusal is never kept.
 */

function sharedPlanCharts(id, full, fresh) {
  const key = `${id}|${full ?? 'all'}`;
  const hit = chartsCache.get(key);
  if (!fresh && hit && Date.now() - hit.at < CHARTS_REUSE_MS) return hit.promise;
  const promise = getJson(`/cpv/plans/${id}/charts/`, full != null ? { full } : undefined);
  chartsCache.set(key, { at: Date.now(), promise });
  promise.catch(() => { if (chartsCache.get(key)?.promise === promise) chartsCache.delete(key); });
  return promise;
}

/** `full` of the plan charts (server chart_view.plan_charts): every item in full (omitted), none ('none': each item as
 * its summary, `summary: true`) or one item id (that item in full, the others as summaries). */
export const CHARTS_SUMMARY = 'none';

export const cpvApi = {
  home: () => getJson('/cpv/home/'),
  riskBands: () => getJson('/cpv/risk-bands/'),
  // CPV-Q12-a: a signed act names the plant the risk-band screen shows (the server's answer for the header choice)
  createBand: (band, reason, esign, plant = null) => postJson('/cpv/risk-bands/', { ...band, reason, esign, ...(plant != null ? { plant } : {}) }),
  replaceBands: (bands, reason, esign, plant = null) => postJson('/cpv/risk-bands/replace/', { bands, reason, esign, ...(plant != null ? { plant } : {}) }),
  // Q4c · the tenant's risk classes (code, name, rank, tone): add, rename (words and tone), deactivate — each signed
  createRiskClass: (row, reason, esign, plant = null) => postJson('/cpv/risk-classes/', { ...row, reason, esign, ...(plant != null ? { plant } : {}) }),
  renameRiskClass: (code, row, reason, esign, plant = null) => postJson(`/cpv/risk-classes/${encodeURIComponent(code)}/rename/`, { ...row, reason, esign, ...(plant != null ? { plant } : {}) }),
  deactivateRiskClass: (code, reason, esign, plant = null) => postJson(`/cpv/risk-classes/${encodeURIComponent(code)}/deactivate/`, { reason, esign, ...(plant != null ? { plant } : {}) }),
  plans: (params) => getJson('/cpv/plans/', params),
  createPlan: (body) => postJson('/cpv/plans/', body),
  plan: (id) => getJson(`/cpv/plans/${id}/`),
  // Q8p: { full, fresh } — see sharedPlanCharts / CHARTS_SUMMARY
  planCharts: (id, { full, fresh = false } = {}) => sharedPlanCharts(id, full, fresh),
  // the chart switcher: one item's data as another view, computed on the server (analysis only unless it is the configured view)
  itemChart: (id, itemId, view) => getJson(`/cpv/plans/${id}/items/${itemId}/chart/`, { view }),
  // CPV-Q6 S1 · the scatter + regression of item y against item x (server fit, band and reading; analysis only)
  regression: (id, x, y) => getJson(`/cpv/plans/${id}/regression/`, { x, y }),
  // CPV-Q5m · batch-level multivariate monitoring of the selected items (server PCA model, T², SPE, contributions,
  // reading; analysis only). params: { items: 'id,id,…', reference, from, to, level, picks }
  multivariate: (id, params) => getJson(`/cpv/plans/${id}/multivariate/`, params),
  // what to pick: suggested parameter groups with their reasons, the parameters left out and why, the suggested reference
  multivariateSuggest: (id) => getJson(`/cpv/plans/${id}/multivariate/suggest/`),
  // CPV-Q6 S3 · an item's tolerance interval (server bounds, k, comparison, reading and rationale words; analysis only).
  // basis: undefined (the lock's baseline, else the admitted series) or 'NEXT_LOCK' (the baseline a lock signed now freezes)
  tolerance: (id, itemId, basis) => getJson(`/cpv/plans/${id}/items/${itemId}/tolerance/`, basis ? { basis } : undefined),
  editPlan: (id, fields) => patchJson(`/cpv/plans/${id}/`, fields),
  addItem: (id, item) => postJson(`/cpv/plans/${id}/items/`, item),
  editItem: (id, itemId, fields) => patchJson(`/cpv/plans/${id}/items/${itemId}/`, fields),
  // RL-PLN-10: rationales = [{designation_code, pair, text, sign_with_act: true}], meanings_shown = exactly what the dialog showed
  // return_to: a return of a plan on the master names its target stage (one of the offer's return_targets)
  act: (id, act, { reason = '', esign = null, rationales = null, meanings_shown = null, return_to = null } = {}) =>
    postJson(`/cpv/plans/${id}/act/`, { act, reason, ...(esign ? { esign } : {}), ...(rationales ? { rationales } : {}),
      ...(meanings_shown ? { meanings_shown } : {}), ...(return_to ? { return_to } : {}) }),
  newVersion: (id) => postJson(`/cpv/plans/${id}/new-version/`, {}),
  lock: (itemId, { reason, esign, change_control_ref = '', rationales = null, meanings_shown = null, tolerance_inputs_hash = null }) =>
    postJson(`/cpv/items/${itemId}/lock/`, { reason, esign, change_control_ref, ...(rationales ? { rationales } : {}),
      ...(meanings_shown ? { meanings_shown } : {}), ...(tolerance_inputs_hash ? { tolerance_inputs_hash } : {}) }),
  vocabulary: (plant = null) => getJson('/cpv/vocabulary/', plant != null ? { plant } : undefined),
  // S4 · the signal register (services/cpv/{inbox,assessment,loop}.py)
  signals: (params) => getJson('/cpv/signals/', params),
  signalCounts: (plan) => getJson('/cpv/signals/counts/', plan ? { plan } : undefined),
  signalVocabulary: () => getJson('/cpv/signals/vocabulary/'),
  signal: (id) => getJson(`/cpv/signals/${id}/`),
  // occurrences_seen (D-13, owner 16 Oct 2026): the episode's hit count the page showed — required by the server, as on assess
  acknowledge: (id, occurrences_seen) => postJson(`/cpv/signals/${id}/acknowledge/`,
    { ...(occurrences_seen ? { occurrences_seen } : {}) }),
  // the rationale is the signature's reason; the decision's QMS records are the server's (CPV ticket adapter)
  // meanings_shown: exactly the meaning the dialog showed (the plan acts' attestation, RL-PLN-10) — the server compares it
  // occurrences_seen (D-13): the episode's hit count the dialog showed — the server's; 409 CPV_SIGNAL_OCCURRENCES_CHANGED when it grew
  assess: (id, { conclusion, decision, rationale, esign, meanings_shown, occurrences_seen }) =>
    postJson(`/cpv/signals/${id}/assess/`, { conclusion, decision, rationale, esign, meanings_shown,
      ...(occurrences_seen ? { occurrences_seen } : {}) }),
  countersign: (assessmentId, esign, meanings_shown, occurrences_seen) => postJson(`/cpv/assessments/${assessmentId}/countersign/`,
    { esign, meanings_shown, ...(occurrences_seen ? { occurrences_seen } : {}) }),
  evaluate: (itemId) => postJson(`/cpv/items/${itemId}/evaluate/`, {}),
  // DD-CPV-48 · "Exclude from CPV": signed, with a reason; revoked only by a new signed act
  exclusions: (itemId) => getJson(`/cpv/items/${itemId}/exclusions/`),
  exclude: (itemId, { batch_number, scope, reason_code, reference, rationale, esign, meanings_shown }) =>
    postJson(`/cpv/items/${itemId}/exclusions/`, { batch_number, scope, reason_code, reference, rationale, esign, meanings_shown }),
  revokeExclusion: (id, { rationale, esign, meanings_shown }) => postJson(`/cpv/exclusions/${id}/revoke/`, { rationale, esign, meanings_shown }),
  // CPV-Q6 S4 · an item's batch values compared by group: query { by: site | campaign | stage | change, and for change one of
  // date | plan_version | master_version } → groups, test, reading, overlay (server statistics; analysis only)
  compare: (id, itemId, query) => getJson(`/cpv/plans/${id}/items/${itemId}/compare/`, query),
  // CPV-Q12-b · an item stratified by production unit (analysis only, MT-7): per-unit summary and Cpk / Ppk, and every
  // drawn batch with its unit
  byUnit: (id, itemId) => getJson(`/cpv/plans/${id}/items/${itemId}/chart/`, { by: 'unit' }),
};
